import { observer } from 'mobx-react'
import React, { ChangeEvent, useState, useCallback } from 'react'
import { useShowDialog } from 'App'
import { OperationNotStarted, OperationStatus } from 'models/common/OperationStatus'
import type { ValidationErrors } from 'models/Vehicles/PartInstanceModel'
import type { ExistingPartNumbers } from 'models/PartNumbers/PartNumbersModel'
import { ExistingPartInstance, ExistingPartInstanceEditingCloneModel, MaintenanceLimitStatus, NewPartInstanceEditingCloneModel, PartInstanceActionLog, PartInstanceEditingClone } from 'models/Vehicles/PartInstanceModel'
import { getLinkForVehiclesPageWithSelectedVehicleAndPart, getLinkForVehiclesPageWithSelectedVehicleParts, getLinkForVehiclesPageWithSpecificVehicleAndEditedPart, getLinkForVehiclesPageWithSpecificVehicleAndNewPart } from 'router'
import { svgNoShrinkClassName } from 'styles/globalCss'
import { getFormattedDateInUserLocaleAndUTC, getFormattedDateTimeInUserLocaleAndUTC } from 'utils/datetimeUtils'
import { PrimaryButton, SecondaryButton } from '../Button/Button'
import { SvgIcons } from '../Icon/Icon'
import { TextInput } from '../Input/Input'
import { DatePicker } from '../DatePicker/DatePicker'
import { DotsMenuButton } from '../MenuButton/MenuButton'
import FetchingWrapper from '../Page/FetchingWrapper'
import { Select } from '../Select/Select'
import { Column, Row, Tbody, TableTitle, TableTitleRow, Td, Th, Thead, Tr, ActionsTd, WrappingTh, TrWithLink, ExtendedViewTr } from '../Table/Table'
import { LoadingOverlay, StyledScrollableTable } from './styled'
import { Label } from '../Typography/Typography'
import noop from 'utils/noop'

interface VehiclePartsTableProps {
    vehicleId: number
    partInstances: Array<ExistingPartInstance>
    editedPartInstance: PartInstanceEditingClone | null
    selectedPartId: number | null
    partInstanceLogs: Map<string, Array<PartInstanceActionLog>>
    fetchPartInstanceLogStatuses: Map<string, OperationStatus>
    fetchPartInstanceLogs: (partId: number) => Promise<void>
    markPartAsUnAirworthy: (vehicleId: number, partId: number) => Promise<void>
    markPartAsInspected: (vehicleId: number, partId: number) => Promise<void>
    removePartFromVehicle: (vehicleId: number, partId: number) => Promise<void>

    partNumbers: Array<ExistingPartNumbers>
    saveEditedPartInstance: () => Promise<boolean>
    saveEditedPartInstanceStatus: OperationStatus
    setEditedPartInstanceFromSerial: (serial: string) => void
}
export const VehiclePartsTable = observer(({ 
    vehicleId, 
    partInstances, 
    editedPartInstance,
    selectedPartId, 
    partInstanceLogs, 
    fetchPartInstanceLogStatuses, 
    fetchPartInstanceLogs,
    markPartAsUnAirworthy,
    markPartAsInspected,
    removePartFromVehicle,
    partNumbers,
    saveEditedPartInstance,
    saveEditedPartInstanceStatus,
    setEditedPartInstanceFromSerial,
 } : VehiclePartsTableProps) => {
    const showTopEditingRow = 
        (ExistingPartInstanceEditingCloneModel.is(editedPartInstance) && !partInstances.map(part => part.id).includes(editedPartInstance.id)) ||
        NewPartInstanceEditingCloneModel.is(editedPartInstance)

    const selectedPartFetchStatus = selectedPartId !== null
        ? (fetchPartInstanceLogStatuses.get(selectedPartId.toString()) ?? OperationNotStarted())
        : null
    const isLoadingPartLogs = selectedPartFetchStatus?.inProgress ?? false

    return (
            <>
            <TableTitleRow>
                <TableTitle {...{ entityCount: partInstances.length, entityName: 'Part' }} />
                <SecondaryButton {...{ 
                            text: 'Add Part', 
                            buttonStyle: 'thin',
                            startComponent: <SvgIcons.PlusSvg />,
                            style: { marginLeft: 'auto'},
                            ...getLinkForVehiclesPageWithSpecificVehicleAndNewPart(vehicleId) }} />
            </TableTitleRow>
            { (partInstances.length > 0 || showTopEditingRow) && 
            <div style={{ position: 'relative' }}>
            {isLoadingPartLogs && <LoadingOverlay />}
            <StyledScrollableTable isLoading={isLoadingPartLogs}>
                <Thead>
                    <Tr>
                        <WrappingTh>Serial #</WrappingTh>
                        <WrappingTh>Part name | Part number</WrappingTh>
                        <WrappingTh>Date Added</WrappingTh>
                        <WrappingTh>Days to inspection</WrappingTh>
                        <WrappingTh>Flight cycles to inspection</WrappingTh>
                        <WrappingTh>Flight hours to inspection</WrappingTh>
                        <WrappingTh>Days / Life limit</WrappingTh>
                        <WrappingTh>Flight cycles / Life limit</WrappingTh>
                        <WrappingTh>Flight hours / Life limit</WrappingTh>
                        <Th></Th>{ /* Actions column */}
                    </Tr>
                </Thead>
                <Tbody>
                    <>
                        { 
                            showTopEditingRow && <PartInstanceEditingRow {... { vehicleId, editedPartInstance, partNumbers, saveEditedPartInstance, saveEditedPartInstanceStatus, setEditedPartInstanceFromSerial }} />
                        }
                        {
                            partInstances.map(partInstance => {
                                const fetchPartInstanceLogsStatus = fetchPartInstanceLogStatuses.get(partInstance.id.toString()) ?? OperationNotStarted()
                                const partLogs = partInstanceLogs.get(partInstance.id.toString())
                                const isSelected = selectedPartId === partInstance.id
                                const isEditedPart = ExistingPartInstanceEditingCloneModel.is(editedPartInstance) && editedPartInstance.id === partInstance.id
                                return isEditedPart ? <PartInstanceEditingRow {... { vehicleId, editedPartInstance, partNumbers, saveEditedPartInstance, saveEditedPartInstanceStatus }} /> :
                                    <React.Fragment {...{ key: `row-${partInstance.id}` }}>
                                        <PartInstanceRow {...{ 
                                            vehicleId, 
                                            isSelected, 
                                            partInstance,
                                            onMarkAsUnairworthy: () => { void markPartAsUnAirworthy(vehicleId, partInstance.id) },
                                            onMarkInspected: () => { void markPartAsInspected(vehicleId, partInstance.id) },
                                            onRemovePartFromVehicle: () => { void removePartFromVehicle(vehicleId, partInstance.id) }
                                            }} />
                                        { isSelected && <ObserverPartInstanceDetailsRow {...{
                                                                fetchPartInstanceLogsStatus, 
                                                                partInstance,
                                                                partInstanceLogs: partLogs,
                                                                refetchPartActionLogs: () => { return fetchPartInstanceLogs(selectedPartId) } }} /> 
                                        }
                                    </React.Fragment>                 
                            } )
                        }
                    </>
                </Tbody>
            </StyledScrollableTable>
            </div> }
        </>
    )
})

interface PartInstanceEditingRowProps {
    vehicleId: number
    editedPartInstance: PartInstanceEditingClone
    partNumbers: Array<ExistingPartNumbers>
    saveEditedPartInstance: () => Promise<boolean>
    saveEditedPartInstanceStatus: OperationStatus
    setEditedPartInstanceFromSerial?: (serial: string) => void
}
const PartInstanceEditingRow = observer(({ vehicleId, editedPartInstance, partNumbers, saveEditedPartInstance, saveEditedPartInstanceStatus, setEditedPartInstanceFromSerial } : PartInstanceEditingRowProps) => {
    const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null)

    const save = useCallback(() => {
        const errors = editedPartInstance.validationErrors

        setValidationErrors(errors)

        if (errors) {
            return
        }
        
        void saveEditedPartInstance().then((saveSuccess: boolean) => {
            if (saveSuccess){
                getLinkForVehiclesPageWithSelectedVehicleParts(vehicleId).push()
            }
        }) 
    }, [editedPartInstance, saveEditedPartInstance])

    return (
        <Tr {...{ isSelected: true }}>
            <Td>
                <TextInput {...{
                    name: 'serial',
                    value: editedPartInstance.serial ?? '',
                    showLabel: false,
                    isValid: !validationErrors?.serial,
                    onChange: ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                        // TODO: existing serial loads existing part info
                        editedPartInstance.setSerial(value)
                        setEditedPartInstanceFromSerial?.(value)
                    },
                }}/>
            </Td>
            <Td>
                <Select {...{
                    name: 'part number',
                    options: partNumbers.map(partNumber => partNumber.asSelectOption),
                    selectedOptions: editedPartInstance.part_number ? [editedPartInstance.part_number.asSelectOption] : [],
                    showLabel: false,
                    onChange: ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
                        const selectedPartNumberId = parseInt(value)
                        const partNumber = partNumbers.find(partNumber => partNumber.id === selectedPartNumberId)
                        if (partNumber !== undefined) {
                            editedPartInstance.setPartNumber(partNumber)
                        }
                    }
                }} />
            </Td>
            <Td>
                <DatePicker {...{
                    selected: editedPartInstance.creationTimestampDate,
                    onChange: (date) => {
                        // Check date is not null (it can be cleared by user)
                        if (date instanceof Date) {
                            editedPartInstance.setCreationTimestamp(date)
                        }
                    }
                }} />
            </Td>
            <Td {...{ colSpan:7 }}>
                <Row style={{ justifyContent: 'flex-end' }}>
                    <SecondaryButton {...{ 
                        text: 'cancel',
                        buttonStyle: 'thin',
                        onClick: () => {
                            getLinkForVehiclesPageWithSelectedVehicleParts(vehicleId).push()
                        }}}/>
                    <PrimaryButton {...{ 
                        text: 'save',
                        buttonStyle: 'thin',
                        onClick: save,
                        isDisabled: saveEditedPartInstanceStatus.inProgress
                    }} />
                </Row>
            </Td>
        </Tr>
    )

})

interface PartInstanceRowProps {
    vehicleId: number,
    isSelected: boolean,
    partInstance: ExistingPartInstance
    onMarkAsUnairworthy: () => void
    onMarkInspected: () => void
    onRemovePartFromVehicle: () => void
}
const PartInstanceRow = observer(({vehicleId, partInstance, isSelected, onMarkAsUnairworthy, onMarkInspected, onRemovePartFromVehicle, ...props } : PartInstanceRowProps) => {
    const showDialog = useShowDialog()
    const rowLink = isSelected ? 
        getLinkForVehiclesPageWithSelectedVehicleParts(vehicleId) :
        getLinkForVehiclesPageWithSelectedVehicleAndPart(vehicleId, partInstance.id)
  
    return (
    <TrWithLink {...{isSelectable: true, isSelected, route: rowLink, ...props}}>
        <Td>
            { partInstance.serial }
        </Td>
        <Td>{ partInstance.part_number.titleAndNumber }</Td>
        <Td>{ getFormattedDateInUserLocaleAndUTC(partInstance.creationTimestampDate) }</Td>
        <Td>
            <PartLimitValueWithIndicator {...{
                value: partInstance.maintenance_in_days, 
                status: partInstance.maintenance_in_days_limit_status }}/>
        </Td>
        <Td>
            <PartLimitValueWithIndicator {...{
                value: partInstance.maintenance_in_flights, 
                status: partInstance.maintenance_in_flights_limit_status }}/>
        </Td>
        <Td>
            <PartLimitValueWithIndicator {...{
                value: partInstance.maintenance_in_flight_hours, 
                status: partInstance.maintenance_in_flight_hours_limit_status }}/>
        </Td>
        <Td>
            <PartLimitValueWithIndicator {...{
                value: partInstance.days_in_use, 
                limit: partInstance.part_number.life_limit_days,
                status: partInstance.days_in_use_limit_status }}/>
        </Td>
        <Td>
            <PartLimitValueWithIndicator {...{
                value: partInstance.flights, 
                limit: partInstance.part_number.life_limit_flights,
                status: partInstance.flights_limit_status }}/>
        </Td>
        <Td>
            <PartLimitValueWithIndicator {...{
                value: partInstance.flight_hours, 
                limit: partInstance.part_number.life_limit_flight_hours,
                status: partInstance.flight_hours_limit_status }}/>
        </Td>
        <ActionsTd>
            <DotsMenuButton {...{
                text: 'Actions',
                menuButtonGroups: [
                  [
                    {
                        text: 'Edit Part',
                        // TODO: maybe better to put the link directly on the menuButton, like this:
                        // href: getLinkForVehiclesPageWithSpecificVehicleAndEditedPart(vehicleId, partInstance.id).link
                        // But right now it causes a full page reload
                        onClick: () => {
                            getLinkForVehiclesPageWithSpecificVehicleAndEditedPart(vehicleId, partInstance.id).push()
                        }
                    },
                    {
                        text: 'Mark as inspected',
                        onClick: () => {
                            showDialog({
                                title: 'Mark part as inspected?',
                                text: `Inspection interval will be reset and a record will be added to ${partInstance.part_number.title}, serial number ${partInstance.serial ?? ''}'s action history.`,
                                confirmText: "Confirm",
                                onConfirmed: onMarkInspected,
                                onCancelled: noop,
                            })
                        },
                      },
                      {
                        text: 'Mark as Unairworthy',
                        onClick: () => {
                            showDialog({
                                title: 'Mark part as Unairworthy?',
                                text: `Are you sure you want to set ${partInstance.part_number.title}, serial number ${partInstance.serial ?? ''} as unworthy? The part will be removed from the vehicle and a record will be added to the part's action log.`,
                                confirmText: "Confirm",
                                onConfirmed: onMarkAsUnairworthy,
                                onCancelled: noop,
                            })
                        },
                      },
                      {
                        text: 'Remove from vehicle',
                        onClick: () => {
                            showDialog({
                                title: 'Remove part from vehicle?',
                                text: `Are you sure you want to remove ${partInstance.part_number.title}, serial number ${partInstance.serial ?? ''} from the vehicle? A record will be added to the part's action log.`,
                                confirmText: "Confirm",
                                onConfirmed: onRemovePartFromVehicle,
                                onCancelled: noop,
                            })
                        },
                      },
                  ],
                ]
            }}/>
        </ActionsTd>
    </TrWithLink>)
})

const PartHealthIndicator = observer(({ limitStatus } : { limitStatus: MaintenanceLimitStatus}) => {
    switch (limitStatus){
        case MaintenanceLimitStatus.HEALTHY:
            return null
        case MaintenanceLimitStatus.REACHED:
            return <SvgIcons.VehicleErrorSvg {...{ className: svgNoShrinkClassName }}/>
        case MaintenanceLimitStatus.UPCOMING:
            return <SvgIcons.VehicleWarningSvg {...{ className: svgNoShrinkClassName }}/>
    }
})


interface PartLimitValueWithIndicatorProps {
    value: number | null
    limit?: number | null
    status: MaintenanceLimitStatus | null
}
const PartLimitValueWithIndicator = ({value, limit, status}: PartLimitValueWithIndicatorProps) => {
    return (<Row>
        {value ?? '--'}{ limit !== undefined && `/${limit ?? '--'}` }
        {status && <PartHealthIndicator {...{ limitStatus : status}} />}
    </Row>)
}


interface PartInstanceDetailsRowProps {
    partInstance: ExistingPartInstance,
    partInstanceLogs: Array<PartInstanceActionLog> | undefined
    fetchPartInstanceLogsStatus: OperationStatus
    refetchPartActionLogs: () => Promise<void>
}

const PartInstanceDetailsRow = ({
  partInstance,
  partInstanceLogs,
  fetchPartInstanceLogsStatus,
  refetchPartActionLogs,
  ...props
}: PartInstanceDetailsRowProps) => {
  return (
    <FetchingWrapper
      {...{
        fetchStatus: fetchPartInstanceLogsStatus,
        inProgressTitle: "fetching part action logs...",
        errorMessagePrefix: "Error fetching part logs: ",
        onRetry: refetchPartActionLogs,
        Container: ({ children }) => <>{children}</>
      }}
    >
      {partInstanceLogs === undefined ? null : (
        <ExtendedViewTr {...{ isSelected: true, ...props }}>
          <Td {...{ colSpan: 10 }}>
            <Column>
              <Label>Action history - {partInstanceLogs.length} records</Label>
              <table>
                <Tbody>
                  {partInstanceLogs.map((partLog) => (
                    <tr {...{ key: partLog.id }}>
                      <Td>{partLog.actionDisplayText}</Td>
                      <Td>{partLog?.user?.name ?? "Unknown user"}</Td>
                      <Td>
                        {partLog.actionTimestamp
                          ? getFormattedDateTimeInUserLocaleAndUTC(
                              partLog.actionTimestamp
                            )
                          : "Unknown timestamp"}{" "}
                        (UTC)
                      </Td>
                    </tr>
                  ))}
                </Tbody>
              </table>
              <Label>Status</Label>
              <span style={{ marginLeft: "1em" }}>
                {partInstance.lifeCycleInfo?.lifeCycleStatusText ??
                  "Status unknown"}
              </span>
            </Column>
          </Td>
        </ExtendedViewTr>
      )}
    </FetchingWrapper>
  );
};

const ObserverPartInstanceDetailsRow = observer(PartInstanceDetailsRow)